import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Tabs, Button, Form, Input, Select, Space, Tag, Popconfirm, Tooltip, Switch, Row, Col,
  Empty, Alert, Divider,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined,
  CheckCircleFilled, WarningFilled, MinusCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import CBInputNumber from '../../components/CBInputNumber';
import CBModal from '../../components/CBModal';
import axiosInstance from '../../variables/api/axiosInstance';
import { can, formatMontant } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';
import { GREEN, ROUGE, API_PARAMETRAGE, Montant } from './commun';
import TableExportButton from '../../components/TableExportButton';

const TYPES_JOURNAL = avecLibelleTraduit([
  { value: 'BANQUE' },
  { value: 'VENTES' },
  { value: 'ACHATS' },
  { value: 'OPERATIONS_DIVERSES' },
  { value: 'SINISTRES' },
], 'comptabilite:typesJournal');
const FORMULE = '__FORMULE__';

// 
// Montants : du code (MONTANT_BASE) vers le français
// 

/** « Honoraires TTC de l'expert — le montant du règlement » → « Honoraires TTC de l'expert ». */
const libelleCourt = (description) => (description || '')
  .split(' — ')[0]
  .replace(/\s*\([^)]*\)\s*$/, '')
  .trim();

/**
 * Traduit l'expression d'une ligne en français. Une variable seule donne son
 * libellé ; « VARIABLE * 5 / 100 » donne « 5 % de … » ; toute autre formule
 * reste affichée telle quelle — mieux vaut la montrer que l'interpréter de travers.
 */
const traduireMontantTexte = (expression, variables) => {
  const expr = String(expression || '').replace(/\s+/g, '');
  const parNom = Object.fromEntries((variables || []).map((v) => [v.nom, libelleCourt(v.description)]));
  if (parNom[expr]) return parNom[expr];
  const pct = expr.match(/^([A-Z_]+)\*([\d.]+)\/100$/);
  if (pct && parNom[pct[1]]) { return i18n.t('comptabilite:parametrage.pourcentageDe', {
      taux: pct[2].replace('.', ','), tauxPoint: pct[2], libelle: parNom[pct[1]].toLowerCase(),
    });
  }
  return null;
};

const traduireMontant = (expression, variables) => traduireMontantTexte(expression, variables)
  || <code style={{ fontSize: 12 }}>{expression}</code>;

// Même grammaire que MontantExpressionEvaluator côté serveur (une variable ou un
// nombre, puis des * et / de gauche à droite). Ne sert qu'à l'aperçu : le serveur
// refait le calcul et reste seul juge de l'équilibre.
const evaluer = (expression, contexte) => {
  if (!expression) return null;
  const jetons = String(expression).replace(/\s+/g, '').split(/([*/])/).filter(Boolean);
  const valeur = (j) => {
    if (Object.prototype.hasOwnProperty.call(contexte, j)) return Number(contexte[j]);
    const n = Number(j);
    return Number.isFinite(n) ? n : null;
  };
  let total = valeur(jetons[0]);
  if (total == null) return null;
  for (let i = 1; i < jetons.length; i += 2) {
    const op = valeur(jetons[i + 1]);
    if (op == null) return null;
    if (jetons[i] === '*') total *= op;
    else if (op === 0) return null;
    else total /= op;
  }
  return Math.round(total * 100) / 100;
};

/** Sélecteur de montant : une liste en français, et une formule libre pour les cas rares. */
function ChoixMontant({ value, onChange, variables }) {
  const { t } = useTranslation('comptabilite');
  const noms = (variables || []).map((v) => v.nom);
  const estVariable = noms.includes(String(value || '').trim());
  const [formule, setFormule] = useState(!estVariable && Boolean(value));

  const options = [
    ...(variables || []).map((v) => ({
      value: v.nom,
      label: <Tooltip title={v.description} placement="left"><span>{libelleCourt(v.description)}</span></Tooltip>,
    })),
    { value: FORMULE, label: <span style={{ color: '#6b7280' }}>{t('parametrage.formulePersonnalisee')}</span> },
  ];

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        value={formule ? FORMULE : value}
        options={options}
        style={{ width: formule ? '45%' : '100%' }}
        getPopupContainer={(t) => t.parentElement}
        onChange={(v) => {
          if (v === FORMULE) { setFormule(true); onChange?.(value || `${noms[0] || 'MONTANT_BASE'} * 5 / 100`); }
          else { setFormule(false); onChange?.(v); }
        }}
      />
      {formule && (
        <Input value={value} onChange={(e) => onChange?.(e.target.value)} maxLength={50}
          placeholder="MONTANT_BASE * 5 / 100" style={{ width: '55%', fontFamily: 'monospace' }} />
      )}
    </Space.Compact>
  );
}

// 
// L'écriture telle qu'elle sera passée (lecture et aperçu)
// 

function ApercuEcriture({ lignes, journal }) {
  const { t } = useTranslation('comptabilite');
  const debit = lignes.reduce((t, l) => t + (l.sens === 'DEBIT' ? Number(l.montant || 0) : 0), 0);
  const credit = lignes.reduce((t, l) => t + (l.sens === 'CREDIT' ? Number(l.montant || 0) : 0), 0);
  const lisible = lignes.every((l) => l.montant != null);
  const ok = lisible && Math.abs(debit - credit) < 0.01;

  const colonnes = [
    {
      title: t('commun.journal'), key: 'j', width: 80,
      render: () => (journal ? <Tag color="blue" style={{ borderRadius: 20 }}>{journal}</Tag> : '—')
    },
    {
      title: t('commun.compte'), key: 'c', width: 260,
      render: (_, l) => (l.numeroCompte ? (
        <span>
          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{l.numeroCompte}</b>
          {l.libelleCompte && <span style={{ color: '#475467' }}> — {l.libelleCompte}</span>}
          {l.compteInactif && <Tag color="red" style={{ marginLeft: 6, borderRadius: 20 }}>{t('parametrage.apercu.inactif')}</Tag>}
        </span>
      ) : <span style={{ color: '#9ca3af' }}>{t('parametrage.apercu.compteAChoisir')}</span>)
    },
    { title: t('parametrage.apercu.montantRetenu'), key: 'm', render: (_, l) => l.libelleMontant },
    {
      title: t('commun.debit'), key: 'd', width: 130, align: 'right',
      render: (_, l) => (l.montant == null && l.sens === 'DEBIT'
        ? <Tag color="red" style={{ borderRadius: 20 }}>{t('parametrage.apercu.illisible')}</Tag>
        : <Montant valeur={l.sens === 'DEBIT' ? l.montant : 0} />)
    },
    {
      title: t('commun.credit'), key: 'cr', width: 130, align: 'right',
      render: (_, l) => (l.montant == null && l.sens === 'CREDIT'
        ? <Tag color="red" style={{ borderRadius: 20 }}>{t('parametrage.apercu.illisible')}</Tag>
        : <Montant valeur={l.sens === 'CREDIT' ? l.montant : 0} />)
    },
  ];

  return (
    <Table size="small" rowKey={(_, i) => i} dataSource={lignes} columns={colonnes} pagination={false}
      summary={() => (
        <Table.Summary.Row style={{ fontWeight: 700, background: ok ? '#f0f9f4' : '#fef3f2' }}>
          <Table.Summary.Cell index={0} colSpan={3}>
            {!lisible
              ? <span style={{ color: ROUGE }}><WarningFilled /> {t('parametrage.apercu.montantIncalculable')}</span>
              : ok
                ? <span style={{ color: GREEN }}><CheckCircleFilled /> {t('commun.equilibree')}</span>
                : <span style={{ color: ROUGE }}><WarningFilled /> {t('parametrage.apercu.desequilibreeRefusee')}</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">{formatMontant(debit)}</Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">{formatMontant(credit)}</Table.Summary.Cell>
        </Table.Summary.Row>
      )} />
  );
}

// 
// Un schéma = une opération métier et son écriture
// 

function SchemaOperation({ regle, comptes, journaux, montantEssai, recharger }) {
  const { t } = useTranslation('comptabilite');
  const [modalOpen, setModalOpen] = useState(false);
  const [lignesSaisies, setLignesSaisies] = useState([]);
  const [journalSaisi, setJournalSaisi] = useState(null);
  const [form] = Form.useForm();
  const modifiable = can(HAB.PARAM_COMPTABLE_UPDATE) && regle.parametrable;
  const variables = regle.variablesDisponibles || [];

  const parNumero = useMemo(() => Object.fromEntries(comptes.map((c) => [c.numeroCompte, c])), [comptes]);

  const lignesLecture = (regle.lignes || []).map((l) => ({
    sens: l.sensLigne,
    numeroCompte: l.numeroCompte,
    libelleCompte: l.libelleLigne || l.libelleCompte,
    compteInactif: !l.compteActif,
    libelleMontant: traduireMontant(l.expressionMontant, variables),
    montant: l.montantSimule,
  }));

  const lignesApercu = lignesSaisies.map((l) => ({
    sens: l?.sensLigne,
    numeroCompte: l?.numeroCompte,
    libelleCompte: l?.libelleLigne || parNumero[l?.numeroCompte]?.libelleCompte,
    libelleMontant: traduireMontant(l?.expressionMontant, variables),
    montant: evaluer(l?.expressionMontant, regle.contexteEssai || {}),
  }));

  const ouvrir = () => {
    form.resetFields();
    const lignes = (regle.lignes || []).map((l) => ({
      numeroCompte: l.numeroCompte, sensLigne: l.sensLigne,
      expressionMontant: l.expressionMontant, libelleLigne: l.libelleLigne,
    }));
    const valeurs = {
      libelle: regle.libelle || regle.libelleTransaction,
      description: regle.description,
      codeJournal: regle.codeJournal,
      actif: regle.actif,
      lignes: lignes.length ? lignes : [
        { sensLigne: 'DEBIT', expressionMontant: variables[0]?.nom || 'MONTANT_BASE' },
        { sensLigne: 'CREDIT', expressionMontant: variables[0]?.nom || 'MONTANT_BASE' },
      ],
    };
    form.setFieldsValue(valeurs);
    setLignesSaisies(valeurs.lignes);
    setJournalSaisi(valeurs.codeJournal);
    setModalOpen(true);
  };

  const enregistrer = async (vals) => {
    try {
      await axiosInstance.put(`${API_PARAMETRAGE}/regles/${regle.typeTransaction}`, {
        ...vals,
        lignes: (vals.lignes || []).map((l, i) => ({ ...l, ordre: i + 1 })),
      }, { params: { montantEssai } });
      toast.success(t('parametrage.messages.schemaEnregistre'));
      setModalOpen(false);
      recharger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.enregistrementImpossible'));
    }
  };

  const optionsComptes = comptes.filter((c) => c.actif).map((c) => ({
    value: c.numeroCompte, label: `${c.numeroCompte} — ${c.libelleCompte}`,
  }));
  const optionsJournaux = journaux.filter((j) => j.actif).map((j) => ({
    value: j.codeJournal, label: `${j.codeJournal} — ${j.libelleJournal}`,
  }));

  return (
    <div className="cb-card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#101828' }}>
            {regle.libelleTransaction}
            {regle.parametrable && (regle.actif
              ? <Tag color="green" style={{ marginLeft: 8, borderRadius: 20 }}>{t('parametrage.active')}</Tag>
              : <Tag style={{ marginLeft: 8, borderRadius: 20 }}>{t('parametrage.desactivee')}</Tag>)}
          </div>
          <div style={{ color: '#667085', fontSize: 12.5, marginTop: 2 }}>
            {regle.codeJournal
              ? (
                <Trans t={t} i18nKey="parametrage.ecriturePasseeDansJournal"
                  values={{ code: regle.codeJournal, libelle: regle.libelleJournal }} components={{ b: <b /> }} />
              )
              : t('parametrage.aucunJournal')}
            {regle.description && <> · {regle.description}</>}
          </div>
        </div>
        {modifiable && (
          <Button icon={<EditOutlined />} onClick={ouvrir}
            style={{ borderColor: GREEN, color: GREEN, borderRadius: 8 }}>
            {regle.lignes?.length ? t('commun.modifier') : t('parametrage.definir')}
          </Button>
        )}
      </div>

      {!regle.parametrable ? (
        <Alert type="info" showIcon style={{ borderRadius: 8 }} message={regle.raisonNonParametrable} />
      ) : !regle.lignes?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('parametrage.aucunSchema')} />
      ) : (
        <ApercuEcriture lignes={lignesLecture} journal={regle.codeJournal} />
      )}

      <CBModal title={t('parametrage.modale.titre', { operation: regle.libelleTransaction })} open={modalOpen}
        onCancel={() => setModalOpen(false)} footer={null} width={1000} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer} style={{ marginTop: 16 }}
          onValuesChange={(_, all) => { setLignesSaisies(all.lignes || []); setJournalSaisi(all.codeJournal); }}>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item name="libelle" label={t('parametrage.modale.libelleEcriture')} rules={[{ required: true, message: t('commun.libelleRequis') }]}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="codeJournal" label={t('commun.journal')} rules={[{ required: true, message: t('parametrage.modale.journalRequis') }]}>
                <Select options={optionsJournaux} getPopupContainer={(t) => t.parentElement} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="actif" label={t('parametrage.active')} valuePropName="checked"
                extra={t('parametrage.modale.aideActive')}>
                <Switch checkedChildren={t('commun.oui')} unCheckedChildren={t('commun.non')} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '4px 0 12px' }}>{t('parametrage.modale.lignesEcriture')}</Divider>

          <Row gutter={8} style={{ color: '#667085', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            <Col span={3}>{t('commun.sens')}</Col>
            <Col span={7}>{t('commun.compte')}</Col>
            <Col span={7}>{t('commun.montant')}</Col>
            <Col span={6}>{t('parametrage.modale.libelleLigneFacultatif')}</Col>
          </Row>

          <Form.List name="lignes">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="top">
                    <Col span={3}>
                      <Form.Item {...rest} name={[name, 'sensLigne']} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                        <Select getPopupContainer={(t) => t.parentElement}
                          options={[{ value: 'DEBIT', label: t('commun.debit') }, { value: 'CREDIT', label: t('commun.credit') }]} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item {...rest} name={[name, 'numeroCompte']} rules={[{ required: true, message: t('parametrage.modale.compteRequis') }]} style={{ marginBottom: 8 }}>
                        <Select showSearch placeholder={t('parametrage.modale.choisirCompte')} options={optionsComptes}
                          optionFilterProp="label" getPopupContainer={(t) => t.parentElement} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item {...rest} name={[name, 'expressionMontant']} rules={[{ required: true, message: t('parametrage.modale.montantRequis') }]} style={{ marginBottom: 8 }}>
                        <ChoixMontant variables={variables} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...rest} name={[name, 'libelleLigne']} style={{ marginBottom: 8 }}>
                        <Input placeholder={t('parametrage.modale.reprisDuCompte')} maxLength={200} />
                      </Form.Item>
                    </Col>
                    <Col span={1} style={{ textAlign: 'center', paddingTop: 4 }}>
                      <Tooltip title={t('parametrage.modale.retirerLigne')}>
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                      </Tooltip>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} style={{ width: '100%', marginTop: 4 }}
                  onClick={() => add({ sensLigne: 'CREDIT', expressionMontant: variables[0]?.nom || 'MONTANT_BASE' })}>
                  {t('parametrage.modale.ajouterLigne')}
                </Button>
              </>
            )}
          </Form.List>

          <div style={{ marginTop: 18, fontSize: 12.5, color: '#667085', marginBottom: 6 }}>
            <Trans t={t} i18nKey="parametrage.modale.apercu"
              values={{ montant: formatMontant(montantEssai) }} components={{ b: <b /> }} />
          </div>
          <ApercuEcriture lignes={lignesApercu} journal={journalSaisi} />

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('commun.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {t('commun.enregistrer')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </div>
  );
}

// 
// Journaux
// 

function Journaux({ journaux, loading, recharger }) {
  const { t } = useTranslation('comptabilite');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const modifiable = can(HAB.PARAM_COMPTABLE_UPDATE);

  const ouvrir = (record) => {
    setEditing(record || null);
    form.resetFields();
    form.setFieldsValue(record || { type: 'BANQUE', actif: true });
    setModalOpen(true);
  };

  const enregistrer = async (vals) => {
    try {
      if (editing) await axiosInstance.put(`${API_PARAMETRAGE}/journaux/${editing.id}`, vals);
      else await axiosInstance.post(`${API_PARAMETRAGE}/journaux`, vals);
      toast.success(editing ? t('parametrage.messages.journalModifie') : t('parametrage.messages.journalCree'));
      setModalOpen(false);
      recharger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.enregistrementImpossible'));
    }
  };

  const supprimer = async (record) => {
    try {
      await axiosInstance.delete(`${API_PARAMETRAGE}/journaux/${record.id}`);
      toast.success(t('parametrage.messages.journalSupprime'));
      recharger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.suppressionImpossible'));
    }
  };

  const libelleType = Object.fromEntries(TYPES_JOURNAL.map((t) => [t.value, t.label]));
  const colonnes = [
    {
      title: t('parametrage.journaux.code'), dataIndex: 'codeJournal', width: 100,
      render: (v) => <Tag color="blue" style={{ fontWeight: 700, borderRadius: 20 }}>{v}</Tag>
    },
    { title: t('commun.libelle'), dataIndex: 'libelleJournal', ellipsis: true },
    { title: t('parametrage.journaux.type'), dataIndex: 'type', width: 170, render: (v) => libelleType[v] || v },
    {
      title: t('comptes.colonnes.utilisePar'), key: 'usage', width: 250,
      render: (_, r) => (
        <span style={{ color: '#475467', fontSize: 12.5 }}>
          {t('parametrage.journaux.utilisation', {
            count: r.nombreRegles, ecritures: r.nombreEcritures, context: r.nombreEcritures > 1 ? 'plusieurs' : 'une',
          })}
        </span>
      )
    },
    {
      title: t('commun.statut'), dataIndex: 'actif', width: 90, align: 'center',
      render: (v) => (v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('commun.actif')}</Tag>
        : <Tag style={{ borderRadius: 20 }}>{t('commun.inactif')}</Tag>)
    },
  ];
  if (modifiable) {
    colonnes.push({
      title: t('commun.actions'), key: 'actions', width: 170, align: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" icon={<EditOutlined />} onClick={() => ouvrir(r)}
            style={{ borderColor: GREEN, color: GREEN, borderRadius: 6 }}>
            {t('commun.modifier')}
          </Button>
          {r.nombreRegles > 0 || r.nombreEcritures > 0 ? (
            <Tooltip title={t('parametrage.journaux.suppressionVerrouillee')}>
              <Button size="small" icon={<DeleteOutlined />} disabled style={{ borderRadius: 6 }} />
            </Tooltip>
          ) : (
            <Popconfirm title={t('parametrage.journaux.confirmerSuppression')} onConfirm={() => supprimer(r)} okText={t('commun.oui')} cancelText={t('commun.non')}>
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <div>
      {modifiable && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => ouvrir(null)}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
            {t('parametrage.journaux.nouveauJournal')}
          </Button>
        </div>
      )}
      <Table className="cb-table" dataSource={journaux} columns={colonnes} rowKey="id" loading={loading}
        pagination={false} />

      <CBModal title={editing ? t('parametrage.journaux.titreModifier', { code: editing.codeJournal }) : t('parametrage.journaux.nouveauJournal')}
        open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={560} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="codeJournal" label={t('parametrage.journaux.code')} rules={[{ required: true, message: t('parametrage.journaux.codeRequis') }]}>
                <Input placeholder="BQ" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="libelleJournal" label={t('commun.libelle')} rules={[{ required: true, message: t('commun.libelleRequis') }]}>
                <Input placeholder={t('parametrage.journaux.placeholderLibelle')} maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="type" label={t('parametrage.journaux.type')} rules={[{ required: true }]}>
                <Select options={TYPES_JOURNAL} getPopupContainer={(t) => t.parentElement} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="actif" label={t('commun.actif')} valuePropName="checked">
                <Switch checkedChildren={t('commun.oui')} unCheckedChildren={t('commun.non')} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('commun.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {editing ? t('commun.enregistrer') : t('parametrage.journaux.creerJournal')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </div>
  );
}

// 
// Page
// 

/**
 * Paramétrage des écritures : pour chaque opération (encaissement, paiement,
 * honoraires, préfinancement…), l'écriture que l'application passe — comptes,
 * sens, montants. Tout vient de la base : changer un compte ici change les
 * écritures à venir, sans redéploiement ; les écritures passées ne bougent pas.
 */
export default function ParametrageEcritures() {
  const { t } = useTranslation('comptabilite');
  const [comptes, setComptes] = useState([]);
  const [journaux, setJournaux] = useState([]);
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [montantEssai, setMontantEssai] = useState(100000);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [c, j, r] = await Promise.all([
        axiosInstance.get(`${API_PARAMETRAGE}/comptes`),
        axiosInstance.get(`${API_PARAMETRAGE}/journaux`),
        axiosInstance.get(`${API_PARAMETRAGE}/regles`, { params: { montantEssai } }),
      ]);
      setComptes(c.data?.data || []);
      setJournaux(j.data?.data || []);
      setRegles(r.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('parametrage.messages.chargementImpossible'));
    } finally {
      setLoading(false);
    }
  }, [montantEssai, t]);

  useEffect(() => { charger(); }, [charger]);

  // Les opérations paramétrables d'abord ; la contre-passation, qui ne se
  // paramètre pas, ferme la liste avec son explication.
  const triees = [...regles].sort((a, b) => Number(b.parametrable) - Number(a.parametrable));

  /*
   * L'export dit la même chose que l'écran, une ligne d'écriture par ligne de
   * tableau : le comptable l'emporte, l'annote et nous le renvoie. Les montants y
   * sont en français — jamais MONTANT_BASE, qui ne veut rien dire hors du logiciel.
   */
  const lignesExport = useMemo(() => {
    const ordonnees = [...regles].sort((a, b) => Number(b.parametrable) - Number(a.parametrable));
    return ordonnees.flatMap((r) => {
      const entete = {
        operation: r.libelleTransaction || r.libelle || r.typeTransaction,
        journal: r.codeJournal ? `${r.codeJournal} — ${r.libelleJournal || ''}`.trim().replace(/ —$/, '') : '—',
        etat: !r.parametrable ? t('parametrage.export.automatique') : (r.actif ? t('parametrage.active') : t('parametrage.export.inactive')),
      };
      if (!r.parametrable) {
        return [{ ...entete, sens: '', compte: '', intitule: r.raisonNonParametrable || '', montant: '', exemple: null }];
      }
      if (!r.lignes?.length) {
        return [{
          ...entete, sens: '', compte: '', intitule: t('parametrage.export.aucuneEcriture'),
          montant: '', exemple: null
        }];
      }
      return r.lignes.map((l) => ({
        ...entete,
        sens: l.sensLigne === 'DEBIT' ? t('commun.debit') : t('commun.credit'),
        compte: l.numeroCompte,
        intitule: l.libelleLigne || l.libelleCompte || '',
        montant: traduireMontantTexte(l.expressionMontant, r.variablesDisponibles) || l.expressionMontant,
        exemple: l.montantSimule,
      }));
    });
  }, [regles, t]);

  const colonnesExport = [
    { title: t('parametrage.export.operation'), dataIndex: 'operation' },
    { title: t('commun.journal'), dataIndex: 'journal' },
    { title: t('parametrage.export.etat'), dataIndex: 'etat' },
    { title: t('commun.sens'), dataIndex: 'sens' },
    { title: t('commun.compte'), dataIndex: 'compte' },
    { title: t('parametrage.export.intituleLigne'), dataIndex: 'intitule' },
    { title: t('commun.montant'), dataIndex: 'montant' },
    { title: t('parametrage.export.exemple'), dataIndex: 'exemple', format: 'currency', numeric: true },
  ];

  const desequilibrees = regles.filter((r) => r.parametrable && r.actif && r.lignes?.length && !r.equilibree);
  const sansSchema = regles.filter((r) => r.parametrable && r.actif && !r.lignes?.length);

  return (
    <Layout title={t('parametrage.titre')}>
      <PageHeader titre={t('parametrage.titre')} icone={<SettingOutlined />}
        compte={regles.length ? regles.filter((r) => r.parametrable).length : null} unite={t('parametrage.unite')} />

      {(desequilibrees.length > 0 || sansSchema.length > 0) && (
        <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
          message={t('parametrage.alerte.titre')}
          description={(
            <div style={{ fontSize: 13 }}>
              {desequilibrees.length > 0 && (
                <div>
                  <Trans t={t} i18nKey="parametrage.alerte.desequilibrees"
                    values={{ operations: desequilibrees.map((r) => r.libelleTransaction).join(', ') }} components={{ b: <b /> }} />
                </div>
              )}
              {sansSchema.length > 0 && (
                <div>
                  <Trans t={t} i18nKey="parametrage.alerte.sansEcriture"
                    values={{ operations: sansSchema.map((r) => r.libelleTransaction).join(', ') }} components={{ b: <b /> }} />
                </div>
              )}
            </div>
          )} />
      )}

      <div className="cb-card" style={{ padding: 16 }}>
        <Tabs
          items={[
            {
              key: 'schemas',
              label: t('parametrage.ongletSchemas'),
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                    <span style={{ color: '#667085', fontSize: 12.5 }}>{t('parametrage.exemplesCalculesSur')}</span>
                    <CBInputNumber min={1000} step={10000} value={montantEssai}
                      onChange={(v) => setMontantEssai(v || 100000)} style={{ width: 150 }} />
                    <span style={{ color: '#667085', fontSize: 12.5 }}>FCFA</span>
                    {/* UN SEUL BOUTON, AVEC SES OPTIONS. `TableExportButton` pose lui-meme le coin
            bas droit, la reserve de place en bas de page, l'etat desactive et le libelle
            de chargement - tout ce que les deux boutons poses a la main refaisaient a leur
            facon, et pas tout a fait pareil d'un ecran a l'autre. */}
                    <TableExportButton
                      floating
                      data={lignesExport}
                      columns={colonnesExport}
                      filename="schemas_ecritures"
                      title={t('parametrage.export.titre')}
                      subtitle={t('parametrage.export.sousTitre', {
                        nombre: regles.filter((r) => r.parametrable).length, montant: formatMontant(montantEssai),
                      })}
                      showTotals={false}
                    />
                  </div>
                  {loading && !regles.length ? (
                    <Empty description={t('commun.chargement')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : triees.map((r) => (
                    <SchemaOperation key={r.typeTransaction} regle={r} comptes={comptes} journaux={journaux}
                      montantEssai={montantEssai} recharger={charger} />
                  ))}
                </>
              ),
            },
            {
              key: 'journaux',
              label: t('parametrage.ongletJournaux'),
              children: <Journaux journaux={journaux} loading={loading} recharger={charger} />,
            },
          ]}
        />
      </div>
    </Layout>
  );
}
